import React from 'react';
import PropTypes from 'prop-types';

function timeout(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function setState(ob, state) {
  return new Promise(resolve => ob.setState(state, resolve));
}

/**
 * An invisible aria-live container with a message to be read by screen readers.
 * Whenever the message gets updated it should be picked up by a screen reader and read out. There are two parameters
 * to control the update: readAfter and clearAfter (both in ms).
 * - readAfter delays the update (and thus the read) for the specified interval
 * - clearAfter clears the message after the specified interval
 * These are necessary for certain cases like when updating the page on a key press, where some screen readers
 * automatically read the page before the key press handling function is executed - thus reading the old state. Delaying
 * the update will override the default read, while clearing it will prevent it to be read again upon next update.
 * You can also manually trigger the read by calling the trigger() function (using react's Refs).
 */
export default class AriaLiveMessage extends React.PureComponent {
  constructor(props) {
    super(props);
    const { message } = this.props;
    this.state = {
      displayMessage: message || '',
      // eslint-disable-next-line react/no-unused-state
      msgId: 0,
    };
  }

  componentDidUpdate(prevProps) {
    const { message: newMessage, readAfter, clearAfter } = this.props;
    if (prevProps.message !== newMessage) {
      this.trigger(newMessage, readAfter, clearAfter);
    }
  }

  trigger = (message, readAfter, clearAfter) => {
    const self = this;
    const msgId = Date.now();

    // first we delete the old message so that readers will detect the change and read out the new message. However
    // if the new message is the same as the old one, some readers (macOS voiceover) won't read it even if we delete
    // the content first. The '\0' character is a hack that will trick the readers to think there was a change.
    const chain = setState(self, { displayMessage: '\0' })
      .then(() => timeout(readAfter || 0))
      .then(() => setState(self, { displayMessage: message, msgId }));

    if (clearAfter || clearAfter === 0) {
      chain
        .then(() => timeout(clearAfter))
        .then(() => {
          // delete only if the message hasn't changed
          if (self.state.msgId === msgId) {
            setState(self, { displayMessage: '' });
          }
        });
    }
  };

  render() {
    const { displayMessage } = this.state;
    return <div data-testid="aria-live-message" className="AT-sronly" aria-live="polite">{displayMessage}</div>;
  }
}

AriaLiveMessage.defaultProps = {
  message: '',
  readAfter: 0,
  clearAfter: null,
};
AriaLiveMessage.propTypes = {
  message: PropTypes.string,
  readAfter: PropTypes.number,
  clearAfter: PropTypes.number,
};